import type { FormlyFieldConfig } from '@ngx-formly/core';

import { UtilityService } from '../../../services/utility.service';

/**
 * Whether a stored answer counts as verified in the read view — the conditions the edit
 * components use to show their check (address.component.ts: `showBagVerify` + a configured
 * `bagApiMappingId` + a BAG check date on the root form; kvk.component.ts: `lookedUp && !manual`).
 * One difference on the address side: here a manual address never reads as verified, while
 * address.component.ts shows its check whenever the root carries a check date.
 *
 * Stored toggles can come back as the string `'true'` (custom-toggle writes strings, see
 * `custom_toggle_string_semantics` project convention), so `manual` is treated as true for
 * either the boolean or the string form.
 */
function isManual(value: unknown): boolean {
  const manual = (value as { manual?: unknown } | null)?.manual;
  return manual === true || manual === 'true';
}

/**
 * Whether this field is configured for BAG verification at all: `showBagVerify` on and a
 * non-empty `bagApiMappingId`. Mirrors the config half of `address.component.ts`'s
 * `isBagVerifyVisible` — NOT its per-role half (`bagVerifyShowUserRole`), which decides who may
 * click verify, not whether the address was ever verified. Without this gate, a
 * `meta-data.bag-last-check-date` set by one address field on the form would read every OTHER
 * address field on that same form as verified too. `showBagVerify` needs checking on its own:
 * switching it off in the form builder hides the mapping id but keeps its value, and the
 * generator still emits it.
 */
export function isBagVerifiable(field: FormlyFieldConfig): boolean {
  if (field.props?.['showBagVerify'] !== true) {
    return false;
  }
  const bagApiMappingId = field.props?.['bagApiMappingId'];
  return typeof bagApiMappingId === 'string' && bagApiMappingId.trim() !== '';
}

/**
 * Mirrors `address.component.ts`'s own read (`ngOnInit`, where it seeds and subscribes
 * `bagLastCheckDate`): the check date lives on the ROOT form group, not on this field's own
 * control, because a BAG verification covers the whole composite address at once.
 * `UtilityService.getFormOfRootForm` is the same root-walk `address.component.ts` itself uses.
 */
export function bagCheckDateControl(field: FormlyFieldConfig) {
  const root = UtilityService.getFormOfRootForm(field);
  return root.formControl?.get('meta-data.bag-last-check-date');
}

/**
 * The manual/date half of the rule, kept separate from `isBagVerifiable` so a caller that already
 * watches the root control's value reactively (e.g. `AddressDisplayComponent`, which cannot rely
 * on a plain field/value snapshot to catch a check date written after first render) can re-run
 * just this check on every emission instead of re-deriving the control from the field each time.
 */
export function isBagCheckVerified(checkDate: unknown, value: unknown): boolean {
  if (!value || typeof value !== 'object' || isManual(value)) {
    return false;
  }
  return !!checkDate;
}

export function isBagVerified(field: FormlyFieldConfig, value: unknown): boolean {
  if (!isBagVerifiable(field)) {
    return false;
  }
  return isBagCheckVerified(bagCheckDateControl(field)?.value, value);
}

/** Mirrors `kvk.component.ts`'s own `confirmed` computed: looked up via KVK, and not manual. */
export function isKvkVerified(value: unknown): boolean {
  return !!value && typeof value === 'object' && (value as { lookedUp?: unknown }).lookedUp === true && !isManual(value);
}
